import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Mail, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { getDigestPreview } from "../../lib/crmApi";
import { sanitizeHtml } from "../../lib/sanitize";

/**
 * "Dagens plan" — in-app preview of the daily 08:00 CPH follow-up digest
 * for the currently logged-in sales rep. Same content that lands in the
 * user's inbox each morning, filtered by lead ownership.
 */
export default function CrmDigest() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("app"); // "app" | "email"

    const load = async () => {
        setLoading(true);
        try {
            setData(await getDigestPreview());
        } catch (e) {
            toast.error("Kunne ikke hente dagens plan");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading || !data) {
        return (
            <div className="p-12 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-4xl" data-testid="crm-digest">
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900">Dagens plan</h1>
                    <p className="text-sm text-slate-600 mt-1">
                        {data.date_label} · {data.count} opfølgning{data.count !== 1 ? "er" : ""} for {data.recipient_email}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                        <button onClick={() => setView("app")}
                            data-testid="digest-view-app"
                            className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                                view === "app" ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}>App-visning</button>
                        <button onClick={() => setView("email")}
                            data-testid="digest-view-email"
                            className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                                view === "email" ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}>Mail-visning</button>
                    </div>
                    <button onClick={load}
                        data-testid="digest-refresh"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 h-8 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <RefreshCw className="h-3.5 w-3.5" /> Opdater
                    </button>
                </div>
            </div>

            {view === "email" ? (
                <div className="rounded-2xl overflow-hidden bg-slate-100 p-6 border border-slate-200" data-testid="digest-email-preview">
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.html) }} />
                </div>
            ) : (
                <AppView items={data.items} />
            )}
        </div>
    );
}

function AppView({ items }) {
    if (items.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="digest-empty">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                    <Calendar className="h-6 w-6" />
                </div>
                <div className="text-lg font-semibold text-slate-900">Ingen opfølgninger i dag</div>
                <p className="text-sm text-slate-500 mt-1">
                    Du er ajour — brug dagen på nye leads eller pipeline-arbejde.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="digest-app-list">
            {items.map((it, idx) => {
                const p = it.payload || {};
                const l = it.lead || {};
                const time = extractTime(p.followup_at);
                return (
                    <div key={it.id || `${it.lead_id}-${idx}`} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-300 transition-colors" data-testid={`digest-item-${idx}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-slate-900 truncate">{l.company || "—"}</span>
                                    <span className="text-sm font-semibold text-sky-700 flex-shrink-0">{time}</span>
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {l.contact_name}
                                    {l.phone && <> · <a href={`tel:${l.phone}`} className="text-slate-600 hover:text-sky-700">{l.phone}</a></>}
                                    {l.email && <> · <a href={`mailto:${l.email}`} className="text-slate-600 hover:text-sky-700">{l.email}</a></>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <KindBadge kind={p.kind} />
                                {l.stage && <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{l.stage}</span>}
                            </div>
                        </div>
                        {p.text && (
                            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">
                                {p.text}
                            </div>
                        )}
                        <div className="mt-3 flex gap-2">
                            {l.phone && (
                                <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 h-8">
                                    <Phone className="h-3.5 w-3.5" /> Ring
                                </a>
                            )}
                            {l.email && (
                                <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 h-8">
                                    <Mail className="h-3.5 w-3.5" /> Skriv
                                </a>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function KindBadge({ kind }) {
    const map = {
        ringt:   { label: "Ringet",  cls: "bg-sky-100 text-sky-800" },
        snakket: { label: "Snakket", cls: "bg-emerald-100 text-emerald-800" },
        mail:    { label: "Mail",    cls: "bg-violet-100 text-violet-800" },
        andet:   { label: "Andet",   cls: "bg-slate-100 text-slate-700" },
    };
    const m = map[kind] || map.andet;
    return <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${m.cls}`}>{m.label}</span>;
}

function extractTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(11, 16); }
}
